/**
 * Custom Quick Add Triggers
 * Integrates price hover/focus and mobile icon with Dawn's existing Quick Add system
 */

class CustomQuickAddTriggers extends HTMLElement {
  constructor() {
    super();
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Combined click handler for both price and mobile triggers
    document.addEventListener("click", (e) => {
      const trigger = e.target.closest(
        ".price-quick-add-trigger, .mobile-quick-add-trigger",
      );
      if (trigger) {
        // Only prevent default if pointer-events are enabled (button is actually clickable)
        const pointerEventsEnabled =
          window.getComputedStyle(trigger).pointerEvents !== "none";
        if (pointerEventsEnabled) {
          e.preventDefault();
          e.stopPropagation(); // Stop event from reaching other handlers
          this.handleTriggerClick(trigger);
        }
        // If pointer-events: none, let the click pass through to image links
      }
    });

    // Keyboard support for both triggers
    document.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        const trigger = e.target.closest(
          ".price-quick-add-trigger, .mobile-quick-add-trigger",
        );
        if (trigger) {
          e.preventDefault();
          this.handleTriggerClick(trigger);
        }
      }
    });
  }

  handleTriggerClick(trigger) {
    // Prevent double-clicks
    if (trigger.disabled || trigger.classList.contains("loading")) {
      return;
    }

    const card = trigger.closest(".card-wrapper");
    if (!card) {
      console.error("CustomQuickAddTriggers: Could not find card wrapper");
      return;
    }

    // Find the existing quick-add button that Dawn created
    const quickAddButton = card.querySelector(".quick-add__submit");

    if (!quickAddButton) {
      console.error("CustomQuickAddTriggers: No quick-add button found");
      return;
    }

    // Add loading state to our custom trigger
    this.setLoadingState(trigger, true);

    // Check if this is a multi-variant product (opens modal)
    const modalOpener = quickAddButton.closest("modal-opener");

    if (modalOpener) {
      // Multi-variant product: Trigger modal opener
      // The modal-opener component handles opening the modal
      quickAddButton.click();

      // Remove loading state after modal opens
      setTimeout(() => {
        this.setLoadingState(trigger, false);
      }, 300);
    } else {
      // Single variant product: Trigger the product-form submission
      const productForm = quickAddButton.closest("product-form");

      if (productForm) {
        // Trigger form submission
        quickAddButton.click();

        // Listen for cart update to remove loading state
        this.waitForCartUpdate(trigger);
      } else {
        console.error("CustomQuickAddTriggers: No product-form found");
        this.setLoadingState(trigger, false);
      }
    }
  }

  setLoadingState(trigger, isLoading) {
    if (isLoading) {
      trigger.disabled = true;
      trigger.classList.add("loading");
      trigger.setAttribute("aria-busy", "true");
    } else {
      trigger.disabled = false;
      trigger.classList.remove("loading");
      trigger.setAttribute("aria-busy", "false");
    }
  }

  waitForCartUpdate(trigger) {
    // Listen for Dawn's cart update events
    const cartUpdateHandler = () => {
      this.setLoadingState(trigger, false);
      document.removeEventListener("cart:updated", cartUpdateHandler);
    };

    document.addEventListener("cart:updated", cartUpdateHandler);

    // Fallback timeout in case event doesn't fire
    setTimeout(() => {
      this.setLoadingState(trigger, false);
      document.removeEventListener("cart:updated", cartUpdateHandler);
    }, 3000);
  }
}

// Initialize
if (!customElements.get("custom-quick-add-triggers")) {
  customElements.define("custom-quick-add-triggers", CustomQuickAddTriggers);
}

// Create and append to body
document.addEventListener("DOMContentLoaded", () => {
  if (!document.querySelector("custom-quick-add-triggers")) {
    const triggers = document.createElement("custom-quick-add-triggers");
    document.body.appendChild(triggers);
  }
});
